import { Buttons } from '../../../controls/buttons';
import { Layouts } from '../../../controls/layouts';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TransportOutConfiguration } from '../../models/transport-configuration';
import { computedAsync } from 'src/app/shared/signal-utilities';
import { TransportService } from '../../services/transport.service';
import { GenericModalService } from 'src/app/shared/generic-modal/generic-modal.service';
import { PartnerService } from 'src/app/partner/services/partner.service';
import { Partner } from 'src/app/partner/models';
import { CurrentUserService } from 'src/app/user/services';
import { PageHeaderComponent } from '../../../controls/components/advanced/page-header/page-header.component';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { IndexCardComponent } from '../../../controls/components/advanced/index-card/index-card.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { TransportConfigurationEditComponent } from '../../transport-configuration-edit.component/transport-configuration-edit.component';

enum Mode { View, Edit, New }
const MaxNumberOfLocationsToShow = 12;

@Component({
    selector: 'app-transport-out-locations-details',
    templateUrl: './transport-out-locations-details.component.html',
    styleUrls: ['./transport-out-locations-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageHeaderComponent, Buttons, SvgIconComponent, Layouts, IndexCardComponent, PermissionRequiredDirective, TransportConfigurationEditComponent]
})
export class TransportOutLocationsDetailsComponent {
  private router = inject(Router);
  private modalService = inject(GenericModalService);
  private activatedRoute = inject(ActivatedRoute);
  private currentUserService = inject(CurrentUserService);
  private transportService = inject(TransportService);
  private partnerService = inject(PartnerService);

  private autoEdit = false;

  locationIds = input.required<string>();
  locationIdsParsed = computed(() =>
    this.locationIds().split(',')
      .map(e => Number(e))
      .filter(n => n > 0)
      .filter((val, idx, self) => self.indexOf(val) === idx) // distinct;
      .slice(0, MaxNumberOfLocationsToShow)
  );

  private refreshCounter = signal(0);

  locations = computedAsync(async () => {
    const ids = this.locationIdsParsed();
    if (ids.length < 1) {
      this.mode.set(Mode.New);
      return [new TransportOutConfiguration()];
    }
    else {
      this.refreshCounter(); // Create a dependency on the refreshCounter signal
      const configs = (await Promise.all(ids.map(id => this.transportService.getTransportOutConfiguration(id)))).filter(c => !!c);
      if (configs.length < 1) {
        this.navigateToList();
      }
      return configs;
    }
  }, []);

  locationPartners = computedAsync(async () => {
    const partnerIds = this.locations()
      .filter(e => e.PartnerId)
      .map(e => e.PartnerId)
      .filter((val, idx, self) => self.indexOf(val) === idx); // distinct;
    const partners = partnerIds.length > 0 ? await this.partnerService.getLightPartners(partnerIds) : [];
    return new Map(partners.map(partner => [partner.PartnersId, partner]));
  }, new Map<number, Partner>());

  selectedLocationIndex = signal(0);

  selectedLocation = computed(() => {
    const locations = this.locations();
    const selectedLocationIndex = this.selectedLocationIndex();
    const location = selectedLocationIndex >= 0 && selectedLocationIndex < locations.length ? locations[selectedLocationIndex] : null;
    return location;
  });

  selectedPartner = computed(() => {
    const locationPartners = this.locationPartners();
    const selectedLocation = this.selectedLocation();
    const partner = selectedLocation?.PartnerId ? locationPartners.get(selectedLocation.PartnerId) ?? null : null;
    return partner;
  });

  private hasManageGlobalPermission = this.currentUserService.hasPermission('Transport_Action_OutgoingLocation_ManageGlobal');
  permissionToManage = computed(() => {
    const selectedLocation = this.selectedLocation();
    return !!selectedLocation && (selectedLocation.PartnerId || this.hasManageGlobalPermission);
  });

  mode = signal(Mode.View);
  viewMode = computed(() => this.mode() === Mode.View);
  newMode = computed(() => this.mode() === Mode.New);

  constructor() {
    // Ensure that selectedLocationIndex is within bounds. It may become -1 in case of an empty list of locations.
    effect(() => {
      const maxIndex = this.locations().length - 1;
      const minIndex = Math.min(0, maxIndex);

      if (this.selectedLocationIndex() < minIndex) {
        this.selectedLocationIndex.set(minIndex);
      }
      else if (this.selectedLocationIndex() > maxIndex) {
        this.selectedLocationIndex.set(maxIndex);
      }
    });

    if (this.router.currentNavigation()?.extras?.state?.autoEdit) {
      this.autoEdit = true;
      this.mode.set(Mode.Edit);
    }
  }

  refresh(): void {
    this.refreshCounter.update(e => e + 1);
  }

  selectLocation(location: TransportOutConfiguration): void {
    const index = this.locations().indexOf(location);
    if (index >= 0) {
      this.selectedLocationIndex.set(index);
    }
  }

  closeLocation(location: TransportOutConfiguration, replaceUrl: boolean): Promise<boolean> {
    const locationIds = location ? this.locationIdsParsed().filter(id => id !== location.Id) : [];
    if (locationIds.length < 1) {
      return this.navigateToList();
    }

    // Keep the selected location unless it's the one being closed
    const currentSelectedId = this.selectedLocation()?.Id;
    const newLocationIndex = locationIds.findIndex(id => id === currentSelectedId);
    if (newLocationIndex >= 0) {
      this.selectedLocationIndex.set(newLocationIndex);
    }
    return this.navigateToDetails(locationIds, replaceUrl);
  }

  edit(): void {
    this.mode.set(Mode.Edit);
  }

  async delete(): Promise<void> {
    try {
      await this.modalService.confirmDirect(`Delete ${this.selectedLocation().Name}`, 'Are you sure you want to delete this location?');
      await this.transportService.deleteTransportOutConfiguration(this.selectedLocation().Id);
      await this.closeLocation(this.selectedLocation(), true);
    }
    catch {
      // User cancelled
    }
  }

  done(): void {
    if (this.newMode() || this.autoEdit) {
      this.mode.set(Mode.View); // Necessary to avoid 'Not saved' dialog after save
      this.navigateToList();
    }
    else {
      this.mode.set(Mode.View);
    }
  }

  navigateToDetails(locationIds: number[], replaceUrl: boolean): Promise<boolean> {
    return this.router.navigate([{ locationIds }], { relativeTo: this.activatedRoute, replaceUrl });
  }

  navigateToList(): Promise<boolean> {
    return this.router.navigate(['..'], { relativeTo: this.activatedRoute });
  }

  canDeactivate = async () => {
    try {
      if (!this.viewMode()) {
        await this.modalService.confirmDirect('Not saved', 'You have not saved your changes. Continue?');
      }
      return true;
    }
    catch {
      return false;
    }
  };
}
